import { startOfDay } from "date-fns";
import React from "react";
import {
  CreateWorkedShiftBody,
  FetchShiftsResponse,
  ShiftObj,
  UpdateWorkedShiftBody,
  WeekDay,
  useCreateWorkedShiftMutation,
  useUpdateWorkedShiftMutation,
} from "store/reducers";
import { v4 } from "uuid";

import { DaySummaryRow } from "./day_summary_row";
import { RosteredShiftRow } from "./RosteredShiftRow";
import { ShiftRow } from "./shift_row";
import { EARNING_TYPES } from "../../attendance/earning_types";
import { LeaveApplicationModal } from "../../leave/leave_application_modal";
import {
  CallBackEventRow,
  getRecordedEventSortTime,
  NewCallBackEventRow,
  NewOnCallEventRow,
  OnCallEventRow,
} from "../OnCallEvent";
import { getTravelEventSortTime, NewTravelEventRow, TravelEvent } from "../TravelEvent";

type Props = {
  day: WeekDay;
  timesheetEmployee: Employee;
  startDate: Date;
  hideArchivedShifts: boolean;
  hideRosteredShifts: boolean;
  workedShiftData: FetchShiftsResponse;
  currentEmployee?: Employee;
  leaveApplications: LeaveApplication[];
};

export type OnUpdateShift = (data: Omit<UpdateWorkedShiftBody, "worked_shift_id">) => void;
export type OnClickSave = (data: CreateWorkedShiftBody) => void;

export function DayComponent(props: Props) {
  const {
    startDate,
    hideArchivedShifts,
    hideRosteredShifts,
    day,
    timesheetEmployee,
    workedShiftData,
    currentEmployee,
    leaveApplications,
  } = props;
  const [leaveModal, setLeaveModal] = React.useState<React.ReactNode>();
  const [isAddingTravelEvent, setIsAddingTravelEvent] = React.useState(false);
  const [eventBeingAdded, setEventBeingAdded] = React.useState<{
    eventType: "on-call" | "call-back";
    defaultRecordedAt: Date;
  }>();
  const [createWorkedShift] = useCreateWorkedShiftMutation();
  const [updateWorkedShift] = useUpdateWorkedShiftMutation();
  const [transientShift, setTransientShift] = React.useState<Partial<WorkedShift>>();

  const onUpdateShiftFactory: (shiftObj: ShiftObj) => OnUpdateShift = (shiftObj: ShiftObj) => (data) => {
    updateWorkedShift({
      worked_shift_id: shiftObj.worked_shift_id,
      ...data,
    });
  };

  const onUpdateTransientShift: OnUpdateShift = (data) => {
    setTransientShift({
      ...transientShift,
      ...data,
    });
  };

  function onClickCreate() {
    setTransientShift({
      id: v4(),
      employee_id: timesheetEmployee.id,
    });
  }

  let onClickSave: OnClickSave = async (data: CreateWorkedShiftBody) => {
    // For transient shifts only
    await createWorkedShift(data);
    setTransientShift(undefined);
  };

  function onClickLeave() {
    setLeaveModal(
      <LeaveApplicationModal
        dismissModal={() => setLeaveModal(undefined)}
        defaults={{
          employee_id: timesheetEmployee.id,
          start: startOfDay(new Date(day.date)),
        }}
      />,
    );
  }

  function onClickTravel() {
    setEventBeingAdded(undefined);
    setIsAddingTravelEvent(true);
  }

  function onClickRecordedEvent(eventType: "on-call" | "call-back", rosteredShift: RosteredShift) {
    setIsAddingTravelEvent(false);
    setEventBeingAdded({ eventType, defaultRecordedAt: new Date(rosteredShift.start) });
  }

  async function revertShiftToRosteredHours(rosteredShift: RosteredShift) {
    await createWorkedShift({
      employee_id: timesheetEmployee.id,
      location_id: rosteredShift.location.id,
      start: rosteredShift.start,
      end: rosteredShift.end,
      xero_EarningsRateName: rosteredShift.department.name === "Training" ? EARNING_TYPES[1].value : undefined,
    });
  }

  // We just revert the first shift for now... if there's multiple rosters the correct one is not easily known
  const firstShift = day.shifts[0];
  async function revertStartTime(rosteredShift: RosteredShift, shiftObj: ShiftObj) {
    await updateWorkedShift({
      worked_shift_id: shiftObj.worked_shift_id,
      start: rosteredShift.start,
    });
  }

  async function revertEndTime(rosteredShift: RosteredShift, shiftObj: ShiftObj) {
    await updateWorkedShift({
      worked_shift_id: shiftObj.worked_shift_id,
      end: rosteredShift.end,
    });
  }

  const onCallEvents = day.on_call_event_ids.flatMap((eventId) => {
    const event = workedShiftData.on_call_events.find((item) => item.id === eventId);
    return event ? [event] : [];
  });
  const callBackEvents = day.call_back_event_ids.flatMap((eventId) => {
    const event = workedShiftData.call_back_events.find((item) => item.id === eventId);
    return event ? [event] : [];
  });
  const onCallEvent = onCallEvents.find((event) => !event.archived);

  const actualRows = [
    ...day.shifts.map((shiftObj) => ({
      sortTime: shiftObj.start ? new Date(shiftObj.start).getTime() : Infinity,
      sortOrder: 0,
      element: (
        <ShiftRow
          key={shiftObj.worked_shift_id}
          shiftObj={shiftObj}
          transientShift={undefined}
          hideArchivedShifts={hideArchivedShifts}
          startDate={startDate}
          day={day}
          timesheetEmployee={timesheetEmployee}
          workedShiftData={workedShiftData}
          onUpdateShift={onUpdateShiftFactory(shiftObj)}
        />
      ),
    })),
    ...day.travel_event_ids.flatMap((travelEventId) => {
      const travelEvent = workedShiftData.travel_events.find((event) => event.id === travelEventId);
      if (!travelEvent || (hideArchivedShifts && travelEvent.archived)) return [];
      return [
        {
          sortTime: getTravelEventSortTime(travelEvent) ?? Infinity,
          sortOrder: 1,
          element: <TravelEvent key={travelEventId} travelEventId={travelEventId} />,
        },
      ];
    }),
    ...onCallEvents.flatMap((event) => {
      if (hideArchivedShifts && event.archived) return [];
      return [
        {
          sortTime: getRecordedEventSortTime(event),
          sortOrder: 2,
          element: <OnCallEventRow key={`on-call-${event.id}`} event={event} />,
        },
      ];
    }),
    ...callBackEvents.flatMap((event) => {
      if (hideArchivedShifts && event.archived) return [];
      return [
        {
          sortTime: getRecordedEventSortTime(event),
          sortOrder: 3,
          element: <CallBackEventRow key={`call-back-${event.id}`} event={event} />,
        },
      ];
    }),
  ].sort((a, b) => a.sortTime - b.sortTime || a.sortOrder - b.sortOrder);

  let rostered_shifts = day.rostered_shift_ids.map((rosteredShiftId) => (
    <RosteredShiftRow
      key={rosteredShiftId}
      rosteredShiftId={rosteredShiftId}
      revertShiftToRosteredHours={revertShiftToRosteredHours}
      revertToShift={firstShift}
      revertStartTime={revertStartTime}
      revertEndTime={revertEndTime}
      onClickOnCall={(rosteredShift) => onClickRecordedEvent("on-call", rosteredShift)}
      onClickCallBack={(rosteredShift) => onClickRecordedEvent("call-back", rosteredShift)}
      onCallEvent={onCallEvent}
      recordedEventIsBeingAdded={eventBeingAdded !== undefined}
    />
  ));

  return (
    <>
      <DaySummaryRow
        day={day}
        employee={timesheetEmployee}
        currentEmployee={currentEmployee}
        onClickCreate={onClickCreate}
        onClickLeave={onClickLeave}
        onClickTravel={onClickTravel}
        workedShiftData={workedShiftData}
        leaveApplications={leaveApplications}
      />
      {actualRows.map((row) => row.element)}
      {isAddingTravelEvent && (
        <NewTravelEventRow employee={timesheetEmployee} day={day} onCancel={() => setIsAddingTravelEvent(false)} />
      )}
      {eventBeingAdded?.eventType === "on-call" && (
        <NewOnCallEventRow
          employee={timesheetEmployee}
          defaultRecordedAt={eventBeingAdded.defaultRecordedAt}
          onCancel={() => setEventBeingAdded(undefined)}
        />
      )}
      {eventBeingAdded?.eventType === "call-back" && (
        <NewCallBackEventRow
          employee={timesheetEmployee}
          defaultRecordedAt={eventBeingAdded.defaultRecordedAt}
          onCancel={() => setEventBeingAdded(undefined)}
        />
      )}
      {transientShift && (
        <ShiftRow
          transientShift={transientShift}
          shiftObj={undefined}
          startDate={startDate}
          day={day}
          timesheetEmployee={timesheetEmployee}
          workedShiftData={workedShiftData}
          onUpdateShift={onUpdateTransientShift}
          onClickSave={onClickSave}
          hideArchivedShifts={false}
        />
      )}
      {!hideRosteredShifts && rostered_shifts}
      {leaveModal}
    </>
  );
}
